"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/shadcn/badge";
import { IntegrationsStatus } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { GmailIcon, OutlookIcon } from "@/components/Icons";
import { CopyButton } from "@/components/UI/CopyButton";
import { InboxSource, InboxSourceConfig } from "../types";
import { StepLayout } from "./HowItWorksSteps";
import { INBOX_SOURCES, isSourceConnected } from "../config";
import { Button } from "@/components/shadcn/button";

interface SourceStepProps {
  selected: InboxSource | null;
  onSelect: (source: InboxSource) => void;
  integrations: IntegrationsStatus | null;
  onNext: (source?: InboxSource) => void;
  onBack: () => void;
  canProceed: boolean;
  nextLabel: string;
  showBack?: boolean;
}

export const SourceStep = ({
  onSelect,
  integrations,
  onNext,
  onBack,
  canProceed,
  nextLabel,
  showBack = true,
}: SourceStepProps) => {
  const { spotGroup, spotTenant } = useSpotUser();

  const externalSources = INBOX_SOURCES.filter(
    (s) => s.category === "external"
  );
  const noxusSource = INBOX_SOURCES.find((s) => s.id === "noxus");

  const tenantSlug =
    spotTenant?.name?.toLowerCase().replace(/[^a-z0-9]/g, "") || "tenant";

  return (
    <StepLayout
      onNext={() => {
        onSelect(null as any);
        onNext(null as any);
      }}
      onBack={onBack}
      disabled={false}
      nextLabel="Connect later"
      nextVariant="outline"
      showNext={true}
      showBack={showBack}
    >
      <div className="text-center space-y-2">
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="text-lg font-medium text-foreground"
        >
          Connect your input channel
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="text-sm text-muted-foreground max-w-md"
        >
          Select the source for this Inbox. Noxus will ingest, digitize,{" "}
          <br></br>and structure every incoming request into a case.
        </motion.p>
      </div>

      <div className="w-full max-w-md space-y-3 pt-4 pb-2">
        {noxusSource && (
          <motion.button
            key={noxusSource.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 0.3,
              duration: 0.4,
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              onSelect(noxusSource.id);
              onNext(noxusSource.id);
            }}
            className={cn(
              "w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all cursor-pointer",
              "border bg-white shadow-sm hover:bg-muted/50"
            )}
          >
            <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-zinc-50 border border-border flex items-center justify-center">
              <div className="w-8 h-8 relative flex items-center justify-center">
                <img
                  src={noxusSource.icon}
                  alt={noxusSource.name}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-sm text-primary">
                  {noxusSource.name}
                </h3>
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary border-0"
                >
                  Instant setup
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed flex items-center gap-1">
                Use a Noxus email to test out
              </div>
            </div>
          </motion.button>
        )}

        {externalSources.map((source, index) => {
          const connected = isSourceConnected(source.id, integrations);
          const isDisabled = source.comingSoon;

          return (
            <motion.button
              key={source.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: isDisabled ? 0.6 : 1 }}
              transition={{
                delay: 0.4 + index * 0.1,
                duration: 0.4,
              }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                if (isDisabled) return;
                onSelect(source.id);
                onNext(source.id);
              }}
              disabled={isDisabled}
              className={cn(
                "w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all",
                "border bg-white shadow-sm hover:bg-muted/50",
                isDisabled
                  ? "cursor-not-allowed pointer-events-none"
                  : "cursor-pointer"
              )}
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-zinc-50 border border-border flex items-center justify-center">
                {source.id === "gmail" ? (
                  <GmailIcon size={32} />
                ) : source.id === "outlook" ? (
                  <OutlookIcon size={32} />
                ) : (
                  <div className="w-8 h-8 relative flex items-center justify-center">
                    <img
                      src={source.icon}
                      alt={source.name}
                      className="w-full h-full object-contain opacity-50"
                    />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-sm text-primary">
                    {source.name}
                  </h3>
                  {source.comingSoon && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0"
                    >
                      Enterprise
                    </Badge>
                  )}
                  {!connected && !source.comingSoon && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 text-muted-foreground"
                    >
                      Not connected
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {source.description}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </StepLayout>
  );
};
